import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, ArrowRight, CheckCircle, Target, Award, 
  Brain, Briefcase, Rocket, BarChart, Users, BookOpen,
  MessageSquare, Star, Zap
} from 'lucide-react';

const PathToSuccessPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-foreground sm:text-5xl">
            Tu Camino al Éxito Profesional
          </h1>
          <p className="mt-4 text-xl text-foreground/80">
            Descubre cómo Cortex AI te guía desde el aprendizaje hasta el éxito en tu carrera
          </p>
        </div>

        {/* Etapas de Aprendizaje */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center">
            <Brain className="h-6 w-6 text-accent mr-2" />
            Etapas de Aprendizaje
          </h2>

          <div className="relative">
            <div className="absolute top-0 bottom-0 left-8 w-px bg-border"></div>
            <div className="space-y-12">
              {/* Nivel Principiante */}
              <div className="relative">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center">
                      <Star className="h-8 w-8 text-accent" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-foreground">Nivel Principiante</h3>
                    <p className="mt-2 text-foreground/80">
                      Comienza tu viaje con los fundamentos esenciales
                    </p>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="dark-card p-4">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-foreground">Fundamentos Teóricos</p>
                            <p className="text-sm text-foreground/70">
                              Aprende los conceptos básicos de tu campo
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="dark-card p-4">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-foreground">Primeras Tareas</p>
                            <p className="text-sm text-foreground/70">
                              Completa ejercicios prácticos guiados
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nivel Intermedio */}
              <div className="relative">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center">
                      <Target className="h-8 w-8 text-accent" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-foreground">Nivel Intermedio</h3>
                    <p className="mt-2 text-foreground/80">
                      Desarrolla habilidades prácticas y experiencia real
                    </p>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="dark-card p-4">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-foreground">Proyectos Prácticos</p>
                            <p className="text-sm text-foreground/70">
                              Trabaja en proyectos del mundo real
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="dark-card p-4">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-foreground">Simulaciones</p>
                            <p className="text-sm text-foreground/70">
                              Enfrenta desafíos de toma de decisiones
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nivel Experto */}
              <div className="relative">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center">
                      <Award className="h-8 w-8 text-accent" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-foreground">Nivel Experto</h3>
                    <p className="mt-2 text-foreground/80">
                      Domina habilidades avanzadas y prepárate para el éxito profesional
                    </p>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="dark-card p-4">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-foreground">Portfolio Profesional</p>
                            <p className="text-sm text-foreground/70">
                              Construye un portfolio impresionante
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="dark-card p-4">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-foreground">Certificación</p>
                            <p className="text-sm text-foreground/70">
                              Obtén certificaciones reconocidas
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certificaciones y Portfolio */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center">
            <Award className="h-6 w-6 text-accent mr-2" />
            Certificaciones y Construcción de Portfolio
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="dark-card p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Certificaciones</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <BookOpen className="h-5 w-5 text-accent mt-1" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-foreground">Evaluación Continua</p>
                    <p className="text-sm text-foreground/70">
                      Tu progreso es evaluado constantemente a través de tareas y proyectos prácticos
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Target className="h-5 w-5 text-accent mt-1" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-foreground">Hitos de Certificación</p>
                    <p className="text-sm text-foreground/70">
                      Obtén certificaciones al completar módulos específicos
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="dark-card p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Portfolio</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Briefcase className="h-5 w-5 text-accent mt-1" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-foreground">Proyectos Reales</p>
                    <p className="text-sm text-foreground/70">
                      Cada tarea y proyecto se convierte en parte de tu portfolio profesional
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <BarChart className="h-5 w-5 text-accent mt-1" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-foreground">Métricas y Resultados</p>
                    <p className="text-sm text-foreground/70">
                      Demuestra el impacto de tu trabajo con datos concretos
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conexión con Empleadores */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center">
            <Users className="h-6 w-6 text-accent mr-2" />
            Conexión con Empleadores
          </h2>

          <div className="dark-card p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Rocket className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Oportunidades Laborales</h3>
                <p className="text-foreground/70">
                  Accede a ofertas de trabajo exclusivas de empresas que buscan talento en nuestra plataforma
                </p>
              </div>
              <div>
                <MessageSquare className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Entrevistas Simuladas</h3>
                <p className="text-foreground/70">
                  Practica entrevistas con IA personalizada según el rol y la empresa
                </p>
              </div>
              <div>
                <Zap className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Matching Inteligente</h3>
                <p className="text-foreground/70">
                  Nuestro algoritmo conecta tu perfil con las empresas más adecuadas
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Después del Entrenamiento */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center">
            <Target className="h-6 w-6 text-accent mr-2" />
            Después del Entrenamiento
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="dark-card p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Para Buscadores de Empleo</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="ml-3 text-foreground/80">
                    Acceso a nuestra red de empresas colaboradoras
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="ml-3 text-foreground/80">
                    Recomendaciones personalizadas de trabajo
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="ml-3 text-foreground/80">
                    Soporte en proceso de entrevistas
                  </span>
                </li>
              </ul>
            </div>

            <div className="dark-card p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Para Emprendedores</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="ml-3 text-foreground/80">
                    Conexión con inversores y mentores
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="ml-3 text-foreground/80">
                    Recursos para lanzamiento de startup
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="ml-3 text-foreground/80">
                    Soporte en estrategia de crecimiento
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            Comenzar mi viaje
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PathToSuccessPage;